import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Check, X, Play, Download, Phone, Cloud, ArrowUpRight, Apple } from "lucide-react";
import { Reveal, AnimatedCounter } from "./motion";
import { useLead } from "./LeadDialog";
import { STATS, FEATURES, COMPARISON, APPS, APP_FEATURES, BENEFITS, SPECS } from "../data";
import { ARTICLES } from "../articles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_SHOTS = [
  { src: "/app_machine_v3.png", label: "Machine status & controls" },
  { src: "/app_faults.png", label: "Live faults & alerts" },
  { src: "https://customer-assets.emergentagent.com/job_proturf-flagship/artifacts/tnyh8wuy_Screenshot_20260629_143911_ProTurf%20Mobile.jpg", label: "Dispatch jobs & set stripe angle" },
  { src: "https://customer-assets.emergentagent.com/job_proturf-flagship/artifacts/ol8mv23u_Screenshot_20260629_143655_ProTurf%20Mobile.jpg", label: "Draw & map your fields" },
];

export const Stats = () => (
  <section id="stats" data-testid="stats" className="bg-[#0A0A0A] border-y border-white/10 py-20 md:py-28 px-6 md:px-12 lg:px-20">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.1} className="text-left">
          <div className="font-heading font-black tracking-[0.02em] text-white text-6xl md:text-7xl lg:text-8xl">
            {s.display
              ? <span>{s.prefix || ""}{s.display}{s.suffix || ""}</span>
              : <AnimatedCounter value={s.value} prefix={s.prefix || ""} suffix={s.suffix || ""} />}
          </div>
          <div className="mt-3 h-1 w-12 bg-[#D62828]" />
          <p className="mt-3 text-sm uppercase tracking-widest text-gray-400">{s.label}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const FeatureMedia = ({ feature: f }) => {
  const videoRef = useRef(null);

  const handleEnter = () => {
    const v = videoRef.current;
    if (v) {
      v.currentTime = 0;
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    }
  };
  const handleLeave = () => {
    const v = videoRef.current;
    if (v) v.pause();
  };

  // Autonomous-style: always-autoplay video
  if (f.video) {
    return (
      <div className="relative h-56 overflow-hidden">
        <video src={f.video} poster={f.img} autoPlay muted loop playsInline
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent pointer-events-none" />
      </div>
    );
  }

  // Hover-to-play: static image transitions into video on hover
  if (f.hoverVideo) {
    return (
      <div className="relative h-56 overflow-hidden" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
        <img src={f.img} alt={f.title}
          className="absolute inset-0 h-full w-full object-cover opacity-70 transition-all duration-700 group-hover:scale-110 group-hover:opacity-0" />
        <video ref={videoRef} src={f.hoverVideo} poster={f.img} muted loop playsInline preload="metadata"
          data-testid={`feature-video-${f.key}`}
          className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-700 group-hover:opacity-100" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent pointer-events-none" />
      </div>
    );
  }

  // LiDAR safety: static image transitions into overhead Navigator with animated radar waves
  if (f.lidarAnim) {
    return (
      <div className="relative h-56 overflow-hidden bg-[#0A0A0A]" data-testid={`feature-lidar-anim-${f.key}`}>
        <img src={f.img} alt={f.title}
          className="absolute inset-0 h-full w-full object-cover opacity-70 transition-opacity duration-700 group-hover:opacity-0" />
        <div className="absolute inset-0 opacity-0 transition-opacity duration-700 group-hover:opacity-100">
          <img src="/navigator_overhead_real.png" alt="Navigator overhead LiDAR scan"
            className="absolute inset-0 h-full w-full object-cover" />
          <div className="lidar-scan absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <span className="lidar-ring" style={{ animationDelay: "0s" }} />
            <span className="lidar-ring" style={{ animationDelay: "0.9s" }} />
            <span className="lidar-ring" style={{ animationDelay: "1.8s" }} />
            <span className="lidar-sweep" />
          </div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent pointer-events-none" />
      </div>
    );
  }

  // Static image only
  return (
    <div className="relative h-56 overflow-hidden">
      <img src={f.img} alt={f.title} className="h-full w-full object-cover opacity-70 transition-transform duration-700 group-hover:scale-110" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent pointer-events-none" />
    </div>
  );
};


export const Features = () => (
  <section id="features" data-testid="features" className="bg-[#0A0A0A] py-24 md:py-32 px-6 md:px-12 lg:px-20">
    <Reveal>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D62828]">Engineered to Lead</p>
      <h2 className="font-heading font-black uppercase tracking-normal text-white text-5xl md:text-7xl mt-2 max-w-3xl leading-[0.9]">
        Intelligence in Every Pass
      </h2>
    </Reveal>
    <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
      {FEATURES.map((f, i) => (
        <Reveal key={f.key} delay={(i % 2) * 0.12}>
          <div data-testid={`feature-${f.key}`} className="group relative h-full border border-white/10 bg-[#121212] overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-0 bg-[#D62828] transition-all duration-500 group-hover:h-full z-10" />
            <FeatureMedia feature={f} />
            <div className="p-8">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D62828]">{f.tag}</p>
              <h3 className="font-heading text-3xl md:text-4xl font-extrabold uppercase tracking-tight text-white mt-2">{f.title}</h3>
              <p className="mt-4 text-gray-400 leading-relaxed text-sm md:text-base">{f.desc}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export const AppShowcase = () => {
  const [zoom, setZoom] = useState(null);
  return (
  <section id="app" data-testid="app-showcase" className="bg-[#0d0d0d] border-y border-white/10 py-24 md:py-32 px-6 md:px-12 lg:px-20">
    {zoom && (
      <div
        data-testid="app-shot-zoom"
        onClick={() => setZoom(null)}
        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 backdrop-blur-sm p-6 cursor-zoom-out animate-in fade-in duration-200"
      >
        <button
          data-testid="app-shot-close"
          onClick={() => setZoom(null)}
          className="absolute top-6 right-6 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-white hover:bg-[#D62828] hover:border-[#D62828] transition-colors"
        >
          <X size={20} />
        </button>
        <div className="flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
          <img src={zoom.src} alt={zoom.label} className="max-h-[80vh] w-auto rounded-[1.6rem] border-2 border-white/20 shadow-[0_30px_90px_rgba(0,0,0,0.8)]" />
          <p className="mt-4 text-sm uppercase tracking-widest text-gray-300">{zoom.label}</p>
        </div>
      </div>
    )}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
      <div>
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D62828]">PROTURF Mobile App</p>
          <h2 className="font-heading font-black uppercase tracking-normal text-white text-5xl md:text-6xl mt-2 leading-[0.9]">
            Your Fleet. In Your Pocket.
          </h2>
          <p className="mt-6 text-gray-400 leading-relaxed text-base md:text-lg">
            Command every Navigator from the PROTURF app. Draw field boundaries, dispatch jobs, monitor machines, and optimize your entire fleet from anywhere on earth.
          </p>
        </Reveal>
        <div className="mt-10 space-y-4">
          {APP_FEATURES.map((feat, i) => (
            <Reveal key={feat} delay={i * 0.1}>
              <div className="flex items-start gap-4 border-b border-white/10 pb-4">
                <div className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center bg-[#D62828]">
                  <Check size={14} className="text-white" />
                </div>
                <p className="text-white text-base md:text-lg">{feat}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-10 flex items-center gap-4 text-gray-400">
            <Cloud size={28} className="text-[#D62828]" />
            <p className="text-sm md:text-base">Every machine streams encrypted telemetry to the cloud for analytics, OTA updates &amp; reporting.</p>
          </div>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a
              data-testid="app-store-link"
              href="https://apps.apple.com/us/app/proturf-mobile/id6742913640"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 border border-white/20 bg-black px-5 py-3 transition-colors hover:border-[#D62828]/70"
            >
              <Apple size={26} className="text-white" />
              <span className="flex flex-col leading-none">
                <span className="text-[10px] uppercase tracking-widest text-gray-400">Download on the</span>
                <span className="text-base font-bold text-white">App Store</span>
              </span>
            </a>
            <a
              data-testid="google-play-link"
              href="https://play.google.com/store/apps/details?id=com.progressiveturfequip.proturfmobileapp"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 border border-white/20 bg-black px-5 py-3 transition-colors hover:border-[#D62828]/70"
            >
              <Play size={24} className="text-white fill-white" />
              <span className="flex flex-col leading-none">
                <span className="text-[10px] uppercase tracking-widest text-gray-400">Get it on</span>
                <span className="text-base font-bold text-white">Google Play</span>
              </span>
            </a>
          </div>
        </Reveal>
      </div>

      <div className="-mx-6 md:-mx-12 lg:mx-0 px-6 md:px-12 lg:px-0 overflow-x-auto">
        <div className="flex gap-4 lg:gap-5 lg:justify-end min-w-min pb-4 snap-x">
          {APP_SHOTS.map((shot, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div
                data-testid={`app-shot-${i}`}
                role="button"
                tabIndex={0}
                onClick={() => setZoom(shot)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setZoom(shot); }}
                className="group/shot snap-start flex-shrink-0 w-36 sm:w-44 cursor-pointer rounded-[1.6rem] border-2 border-white/15 bg-black p-1.5 shadow-[0_20px_60px_rgba(0,0,0,0.6)] hover:border-[#D62828]/60 transition-colors"
              >
                <img src={shot.src} alt={shot.label} className="w-full rounded-[1.2rem] object-cover transition-transform duration-300 group-hover/shot:scale-[1.03]" loading="lazy" />
                <div className="mt-2 px-1 pb-1 text-[10px] sm:text-xs text-center text-gray-400 leading-tight">{shot.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
  );
};

export const Comparison = () => (
  <section id="compare" data-testid="comparison" className="bg-[#0A0A0A] py-24 md:py-32 px-6 md:px-12 lg:px-20">
    <Reveal>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D62828]">The Difference</p>
      <h2 className="font-heading font-black uppercase tracking-normal text-white text-5xl md:text-7xl mt-2 leading-[0.9]">
        Navigator vs. Traditional
      </h2>
    </Reveal>
    <Reveal delay={0.1}>
      <div className="mt-14 border border-white/10">
        <div className="grid grid-cols-3 bg-[#121212] border-b border-white/10">
          <div className="p-5 text-xs md:text-sm font-bold uppercase tracking-widest text-gray-400">Metric</div>
          <div className="p-5 text-xs md:text-sm font-bold uppercase tracking-widest text-gray-500 border-x border-white/10">Traditional</div>
          <div className="p-5 text-xs md:text-sm font-bold uppercase tracking-widest text-[#D62828]">Navigator</div>
        </div>
        {COMPARISON.map((row, i) => (
          <div key={row.metric} data-testid={`compare-row-${i}`} className={`grid grid-cols-3 ${i % 2 ? "bg-[#0d0d0d]" : "bg-[#0A0A0A]"} border-b border-white/10 last:border-0`}>
            <div className="p-5 text-sm md:text-base text-white font-medium">{row.metric}</div>
            <div className="p-5 text-sm md:text-base text-gray-500 border-x border-white/10 flex items-center gap-2">
              <X size={16} className="text-gray-600 flex-shrink-0" /> {row.traditional}
            </div>
            <div className="p-5 text-sm md:text-base text-white flex items-center gap-2 font-semibold">
              <Check size={16} className="text-[#D62828] flex-shrink-0" /> {row.navigator}
            </div>
          </div>
        ))}
      </div>
    </Reveal>
  </section>
);

export const Applications = () => (
  <section id="applications" data-testid="applications" className="bg-[#0d0d0d] border-y border-white/10 py-24 md:py-32 px-6 md:px-12 lg:px-20">
    <Reveal>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D62828]">Built for Scale</p>
      <h2 className="font-heading font-black uppercase tracking-normal text-white text-5xl md:text-7xl mt-2 leading-[0.9]">
        Ideal Applications
      </h2>
      <p className="mt-5 max-w-2xl text-gray-400 text-base md:text-lg">
        From sod farms and polo fields to airports, sports complexes and private acreage, Navigator scales to the most demanding grounds.
      </p>
    </Reveal>
    <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {APPS.map((a, i) => (
        <Reveal key={a.title} delay={(i % 3) * 0.1}>
          <div data-testid={`application-${i}`} className="group relative h-80 overflow-hidden border border-white/10">
            <img src={a.img} alt={a.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent group-hover:from-[#D62828]/80 group-hover:via-black/40 transition-all duration-500" />
            <div className="absolute bottom-0 p-6">
              <h3 className="font-heading text-3xl font-extrabold uppercase tracking-tight text-white">{a.title}</h3>
              <p className="mt-2 text-sm text-gray-300 opacity-0 max-h-0 group-hover:opacity-100 group-hover:max-h-24 transition-all duration-500 overflow-hidden">{a.desc}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export const VideoSection = () => {
  const [playing, setPlaying] = useState(false);
  return (
    <section data-testid="video-section" className="relative bg-[#0A0A0A] py-24 md:py-32 px-6 md:px-12 lg:px-20">
      <Reveal>
        <div
          className="relative mx-auto aspect-video w-full overflow-hidden border border-white/10 bg-black"
          style={{ maxWidth: "min(100%, calc((100vh - 10rem) * 16 / 9))" }}
        >
          {playing ? (
            <video data-testid="video-player" className="absolute inset-0 h-full w-full bg-black"
              src="https://customer-assets.emergentagent.com/job_proturf-flagship/artifacts/el5mopuu_NAVIGATOR_horizontal_1.mp4"
              controls autoPlay playsInline />
          ) : (
            <>
              <video
                src="https://customer-assets.emergentagent.com/job_proturf-flagship/artifacts/el5mopuu_NAVIGATOR_horizontal_1.mp4"
                muted loop autoPlay playsInline
                className="h-full w-full object-cover opacity-50" />
              <div className="absolute inset-0 bg-black/30" />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <button data-testid="video-play-btn" onClick={() => setPlaying(true)}
                  className="flex h-20 w-20 items-center justify-center rounded-full bg-[#D62828] shadow-[0_0_40px_rgba(214,40,40,0.6)] hover:scale-110 transition-transform">
                  <Play size={30} className="text-white ml-1" fill="white" />
                </button>
                <p className="mt-6 font-heading text-3xl md:text-5xl font-extrabold uppercase tracking-tight text-white">See Navigator In Action</p>
              </div>
            </>
          )}
        </div>
      </Reveal>
    </section>
  );
};

const ArticleModal = ({ article, onClose }) => {
  useEffect(() => {
    if (article) {
      const onKey = (e) => e.key === "Escape" && onClose();
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
      return () => {
        document.removeEventListener("keydown", onKey);
        document.body.style.overflow = "";
      };
    }
  }, [article, onClose]);

  if (!article) return null;

  return (
    <div
      data-testid="article-modal"
      className="fixed inset-0 z-[70] flex justify-center bg-black/80 backdrop-blur-sm p-4 sm:p-8 overflow-y-auto animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="relative my-4 w-full max-w-3xl border border-white/10 bg-[#0A0A0A] shadow-[0_30px_90px_rgba(0,0,0,0.8)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-[#0A0A0A]/95 backdrop-blur px-6 sm:px-10 py-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D62828]">Progressive Navigator</p>
          <button data-testid="article-close-btn" onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 text-white hover:bg-[#D62828] hover:border-[#D62828] transition-colors">
            <X size={18} />
          </button>
        </div>
        <article className="px-6 sm:px-10 py-8 sm:py-10">
          <h2 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-normal text-white leading-[0.95]">{article.title}</h2>
          <p className="mt-4 text-lg text-[#D62828] font-semibold">{article.subtitle}</p>
          <div className="mt-8 h-1 w-16 bg-[#D62828]" />
          <div className="mt-8 space-y-5">
            {article.blocks.map((blk, i) => {
              if (blk.h) return <h3 key={i} className="font-heading text-2xl font-extrabold uppercase tracking-tight text-white pt-3">{blk.h}</h3>;
              if (blk.p) return <p key={i} className="text-gray-300 leading-relaxed text-base">{blk.p}</p>;
              if (blk.ul) return (
                <ul key={i} className="space-y-2 pl-1">
                  {blk.ul.map((it, j) => (
                    <li key={j} className="flex items-start gap-3 text-gray-300 text-base">
                      <Check size={18} className="mt-1 flex-shrink-0 text-[#D62828]" /> <span>{it}</span>
                    </li>
                  ))}
                </ul>
              );
              if (blk.table) return (
                <div key={i} className="overflow-hidden border border-white/10">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="bg-[#121212]">
                        {blk.table.head.map((h, j) => <th key={j} className="px-4 py-3 font-bold uppercase tracking-wider text-[#D62828] text-xs">{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {blk.table.rows.map((row, j) => (
                        <tr key={j} className="border-t border-white/10">
                          {row.map((c, k) => <td key={k} className={`px-4 py-3 ${k === 0 ? "text-white font-semibold" : "text-gray-300"}`}>{c}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
              return null;
            })}
          </div>
        </article>
      </div>
    </div>
  );
};

export const Benefits = () => {
  const [openIdx, setOpenIdx] = useState(null);
  const article = openIdx !== null ? ARTICLES[openIdx] : null;
  return (
  <section id="benefits" data-testid="benefits" className="bg-[#0d0d0d] border-y border-white/10 py-24 md:py-32 px-6 md:px-12 lg:px-20">
    <Reveal>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D62828]">Performance &amp; Maintenance</p>
      <h2 className="font-heading font-black uppercase tracking-normal text-white text-5xl md:text-7xl mt-2 leading-[0.9]">
        Less Downtime. More Done.
      </h2>
    </Reveal>
    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
      {BENEFITS.map((b, i) => (
        <Reveal key={b.title} delay={(i % 2) * 0.1}>
          <button
            type="button"
            data-testid={`benefit-${i}`}
            onClick={() => setOpenIdx(i)}
            className="group w-full text-left border border-white/10 bg-[#0A0A0A] p-8 hover:border-[#D62828]/50 hover:bg-[#0f0f0f] transition-colors cursor-pointer h-full"
          >
            <div className="flex items-center justify-between">
              <span className="font-heading text-2xl font-extrabold text-[#D62828]">0{i + 1}</span>
              <ArrowUpRight className="text-gray-600 group-hover:text-[#D62828] transition-colors" />
            </div>
            <h3 className="font-heading text-3xl font-extrabold uppercase tracking-tight text-white mt-4">{b.title}</h3>
            <p className="mt-3 text-gray-400 leading-relaxed text-sm md:text-base">{b.desc}</p>
            <span className="mt-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#D62828]">
              Read the article <ArrowUpRight size={14} />
            </span>
          </button>
        </Reveal>
      ))}
    </div>
    <ArticleModal article={article} onClose={() => setOpenIdx(null)} />
  </section>
  );
};

export const Specifications = () => (
  <section id="specs" data-testid="specifications" className="bg-[#0A0A0A] py-24 md:py-32 px-6 md:px-12 lg:px-20">
    <Reveal>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D62828]">By The Numbers</p>
      <h2 className="font-heading font-black uppercase tracking-normal text-white text-5xl md:text-7xl mt-2 leading-[0.9]">
        Specifications
      </h2>
    </Reveal>
    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-16">
      {SPECS.map((s, i) => (
        <Reveal key={s.label} delay={(i % 2) * 0.05}>
          <div data-testid={`spec-${i}`} className="flex items-baseline justify-between gap-6 border-b border-white/10 py-5">
            <span className="text-xs uppercase tracking-widest text-gray-400 flex-shrink-0">{s.label}</span>
            <span className="font-heading text-lg md:text-2xl font-bold text-white text-right">{s.value}</span>
          </div>
        </Reveal>
      ))}
    </div>
    <Reveal delay={0.2}>
      <p className="mt-8 text-xs text-gray-600 max-w-2xl">Mowing capacity assumes no overlap or turning. Under our policy of continuous improvement, specifications are subject to change without notice.</p>
    </Reveal>
  </section>
);


export const CtaFooter = () => {
  const { openLead } = useLead();

  return (
    <>
      <section id="brochure" data-testid="cta-footer" className="relative bg-[#0A0A0A] py-28 md:py-40 px-6 md:px-12 lg:px-20 overflow-hidden grain">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(214,40,40,0.18),transparent_55%)]" />
        <div className="relative z-10 max-w-4xl">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D62828]">Ready When You Are</p>
            <h2 className="font-heading font-black uppercase tracking-normal text-white text-6xl md:text-8xl mt-2 leading-[0.85]">
              Automate Your Turf
            </h2>
            <p className="mt-6 max-w-xl text-gray-300 text-base md:text-lg leading-relaxed">
              Join the grounds teams already running on Navigator. Book a live demo, request pricing, or download the full spec brochure.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4">
              <button data-testid="footer-demo-btn" onClick={() => openLead("demo")}
                className="bg-[#D62828] text-white hover:bg-[#B91C1C] px-9 py-4 text-sm font-bold uppercase tracking-widest transition-all shadow-[0_0_25px_rgba(214,40,40,0.5)]">
                Book a Demo
              </button>
              <button data-testid="footer-quote-btn" onClick={() => openLead("quote")}
                className="border border-white/40 text-white hover:bg-white hover:text-black px-9 py-4 text-sm font-bold uppercase tracking-widest transition-colors">
                Request a Quote
              </button>
              {/* No `download` attr: it is blocked inside the Emergent preview's sandboxed iframe. The endpoint serves the PDF inline, so target=_blank opens & renders it in a new tab (Save available in the viewer). */}
              <a data-testid="brochure-download-btn" href={`${API}/brochure`} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 border border-white/40 text-white hover:bg-white hover:text-black px-9 py-4 text-sm font-bold uppercase tracking-widest transition-colors">
                <Download size={16} /> Brochure (PDF)
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="dealer-support" className="bg-[#0d0d0d] border-y border-white/10 py-16 px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <Reveal>
            <h3 className="font-heading text-4xl font-extrabold uppercase tracking-tight text-white">Nationwide Dealer Support</h3>
            <p className="mt-3 text-gray-400 text-base">Certified Progressive Turf Equipment dealers provide on-site setup, training, and rapid-response service across North America.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex md:justify-end">
              <a href="tel:+18006688873" data-testid="dealer-call-btn"
                className="flex items-center gap-3 border border-[#D62828] text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-[#D62828] transition-colors">
                <Phone size={18} /> Connect / Inquire&nbsp;&middot;&nbsp;1-800-668-8873
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <footer data-testid="footer" className="bg-[#0A0A0A] py-12 px-6 md:px-12 lg:px-20">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-baseline gap-2">
            <span className="font-heading text-xl font-extrabold tracking-tight text-white">PROGRESSIVE</span>
            <span className="font-heading text-xl font-extrabold tracking-tight text-[#D62828]">NAVIGATOR</span>
          </div>
          <p className="text-sm text-gray-500">&copy; {new Date().getFullYear()} Progressive Turf Equipment Inc. Autonomous turf maintenance, perfected.</p>
        </div>
      </footer>
    </>
  );
};
